import { useId, useState } from 'react';
import type { CapiumTagGroup } from '@/core/models/capium-tags';
import { CapiumTagsPanel } from '@/kit/CapiumTagsPanel';
import './NoteTagsTab.scss';

interface NoteTagsTabProps {
  groups: CapiumTagGroup[];
  /** Called with the tag when it could not be placed at the cursor (as `CapiumTagsPanel`'s `onInsert`). */
  onInsert: (tag: string) => void;
  /** Open on first render. Closed by default (2 Nov review): the editor gets the room; a click on the Tags tab opens the list. */
  defaultOpen?: boolean;
}

/**
 * Design review 2 Nov 2026: the fixed "Capium Tags" side panel beside a note editor "is also not good" - the tags become a tab.
 * The tab sits at the editor's edge and shows or hides the same tag list (`CapiumTagsPanel`, unchanged: click or double-click inserts a
 * tag at the cursor). The editor stays on screen and keeps its cursor: a press on the tab does not take focus out of it, so a tag
 * still lands where the cursor was.
 */
export function NoteTagsTab({ groups, onInsert, defaultOpen = false }: NoteTagsTabProps) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();

  return (
    <div className={`note-tags-tab${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="note-tags-tab__tab"
        aria-expanded={open}
        aria-controls={panelId}
        // Keep the cursor in the note editor: the tab must not take the focus the tag would be inserted at.
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => setOpen((current) => !current)}
      >
        Tags
      </button>
      <div id={panelId} className="note-tags-tab__panel" hidden={!open}>
        {open && <CapiumTagsPanel groups={groups} onInsert={onInsert} />}
      </div>
    </div>
  );
}
